import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Popover } from '@mui/material';
import { t } from 'i18next';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';

type P = {
  checked: boolean;
  onChange: (all: boolean) => void;
};

const OPTIONS: { all: boolean; label: string }[] = [
  { all: false, label: '한 명이라도 포함' },
  { all: true, label: '모두 포함' },
];

/**
 * 사람 필터의 AND(모두 포함)/OR(한 명이라도 포함) 선택. 결재자/협조 결재자/내부 참석자
 * 필터에서 공용으로 쓴다. 상태는 호출부가 checked/onChange 로 주입한다.
 * 시안: 팝오버 상단 "필터 옵션 | [선택값 ▾]" 헤더에서 드롭다운으로 전환.
 */
const AllFilterOption = ({ checked, onChange }: P) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const isAll = checked;
  const currentLabel = isAll ? '모두 포함' : '한 명이라도 포함';

  const pick = (all: boolean) => {
    onChange(all);
    setOpen(false);
  };

  return (
    <Header
      ref={headerRef}
      open={open}
      onClick={() => {
        // 열려 있을 때의 클릭은 Popover backdrop 이 onClose 로 닫으므로, 여기선 열기만 한다
        if (!open) setOpen(true);
      }}
    >
      <HeaderLabel>{t('필터 옵션')}</HeaderLabel>
      <Divider />
      <ModePill>{t(currentLabel)}</ModePill>
      <Chevron open={open} aria-hidden>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M8 5.833L12.167 10L8 14.167"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Chevron>
      {/* 시안: 옆(오른쪽)으로 열리는 별도 패널 */}
      <Popover
        open={open}
        anchorEl={headerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            marginLeft: '8px',
            padding: '8px',
            minWidth: '200px',
            background: 'var(--dk-bg, #ffffff)',
            border: '1px solid black',
            borderRadius: '8px',
            boxShadow: '0px 16px 32px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
          },
        }}
      >
        {OPTIONS.map((o) => (
          <OptionRow
            key={o.label}
            className={'v2rd-hover-target'}
            onClick={() => pick(o.all)}
          >
            <RadioV2 checked={o.all === isAll} sx={{ padding: 0 }} />
            <span>{t(o.label)}</span>
          </OptionRow>
        ))}
      </Popover>
    </Header>
  );
};

export default AllFilterOption;

/** 팝오버 최상단 full-bleed 헤더 (패널 패딩 0). 기본 흰색, 열렸을 때만 회색 */
const Header = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 12px 0 16px;
  border-radius: 8px 8px 0 0;
  background: ${({ open }) => (open ? 'var(--dk-bg-subtle, #f9fafb)' : 'var(--dk-bg, #ffffff)')};
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
  cursor: pointer;
`;

const HeaderLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text-strong, #5f636b);
  flex-shrink: 0;
`;

const Divider = styled.span`
  width: 1px;
  height: 12px;
  background: var(--dk-bg-strong, #d4d7db);
  flex-shrink: 0;
`;

const ModePill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  box-sizing: border-box;
  padding: 4px 8px;
  background: var(--dk-bg, #ffffff);
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 4px;
  color: var(--dk-accent, #0066cc);
  font-size: 14px;
  line-height: 20px;
`;

const Chevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  margin-left: auto;
  color: var(--dk-text, black);
  transition: transform 0.15s ease;
  transform: rotate(${({ open }) => (open ? '90deg' : '0deg')});
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
  cursor: pointer;
  * {
    cursor: pointer;
  }
  &:hover {
    background: var(--dk-bg-subtle, #f9fafb);
  }
`;
